import { ReactElement } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import Skeleton from '@mui/material/Skeleton';
import { CardItem } from './types/support';
import { ContentCardProps } from './types/content-props';
import { ModalContentCard } from './modal-content-card';
import { ModalSupportCard } from './modal-support-card';
import { ModalExceptionCard } from './modal-exception-card';
import { modalContentColumns, ModalContentTable } from './modal-content-table';
import { ExceptionCardInfo } from './types/exception';

export interface MainContentProps {
  contentDetails: ContentCardProps;
  cardItems: CardItem[];
  exceptionItems?: ExceptionCardInfo[];
  isFrameworkMatched: boolean;
  loading?: boolean;
}

// eslint-disable-next-line max-lines-per-function
const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    gap: '16px',
    height: 'auto',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      height: 'auto',
      padding: '16px',
    },
  },
  contentCard: {
    flex: '0 1 70%',
    maxWidth: '70%',
    [theme.breakpoints.down('sm')]: {
      flex: '1 0 auto',
      height: 'auto',
      maxWidth: '100%',
    },
  },
  supportCard: {
    flex: '0 1 30%',
    maxWidth: '30%',
    [theme.breakpoints.down('sm')]: {
      flex: '1 0 auto',
      height: 'auto',
      maxWidth: '100%',
    },
  },
  supportCardContainer: {
    flex: '0 1 30%',
    maxWidth: '30%',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    [theme.breakpoints.down('sm')]: {
      flex: '1 0 auto',
      height: 'auto',
      maxWidth: '100%',
    },
  },
  tableBoxContainer: {
    width: 'auto',
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'row',
    overflowX: 'auto',
    paddingBottom: '16px',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&::-webkit-scrollbar': {
      height: '8px',
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&::-webkit-scrollbar-track': {
      background: '#E8E8E8',
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&::-webkit-scrollbar-thumb': {
      background: '#9D9EA4',
      borderRadius: '50px',
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#555',
    },
  },
}));

export const ModalMainContent = (props: MainContentProps): ReactElement => {
  const {
    cardItems,
    contentDetails,
    exceptionItems,
    isFrameworkMatched,
    loading,
  } = props;
  const { gridRows } = contentDetails;
  const filteredColumns = modalContentColumns.filter(column =>
    gridRows?.some(
      row => column.field !== 'severity' || (row.severity ?? false),
    ),
  );
  const classes = useStyles();

  return (
    <>
      <Box className={`${classes.container} ${classes.tableBoxContainer}`}>
        <ModalContentTable
          columns={filteredColumns}
          rows={gridRows}
          isFrameworkMatched={isFrameworkMatched}
        />
      </Box>
      {loading ? (
        <Box className={classes.container}>
          <div className={classes.contentCard}>
            <Skeleton variant="text" width="40%" height={28} />
            <Skeleton
              variant="rectangular"
              width="100%"
              height={120}
              style={{ marginTop: 12 }}
            />
            <Skeleton
              variant="text"
              width="40%"
              height={28}
              style={{ marginTop: 24 }}
            />
            <Skeleton
              variant="rectangular"
              width="100%"
              height={80}
              style={{ marginTop: 12 }}
            />
          </div>
          <div className={classes.supportCardContainer}>
            <Skeleton variant="rectangular" width="100%" height={160} />
          </div>
        </Box>
      ) : (
        <Box className={classes.container}>
          <div className={classes.contentCard}>
            <ModalContentCard {...contentDetails} />
          </div>
          <div className={classes.supportCardContainer}>
            {exceptionItems && exceptionItems.length > 0 && (
              <ModalExceptionCard exceptionCardItems={exceptionItems} />
            )}
            <ModalSupportCard cardItems={cardItems} />
          </div>
        </Box>
      )}
    </>
  );
};
